import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress,
  IconButton, Stack, TableCell, TableRow, Typography,
  useMediaQuery, useTheme
} from '@mui/material';
import { Add, Delete as DeleteIcon, Edit as EditIcon, Refresh } from '@mui/icons-material';
import { reportCardAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import { DataTable, TD_SX, useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import ConductRatingFormDialog from './forms/ConductRatingFormDialog';
import BulkConductRatingDialog from './forms/BulkConductRatingDialog';

const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};
const getErrorMessage = (e, fallback = 'Operation failed') =>
  e?.response?.data?.message || e?.response?.data?.error || fallback;
const getRatingName = (r) => r?.rating_name || r?.name || 'Unknown';
const getRatingCode = (r) => r?.rating_code || r?.code || '-';

const INITIAL_FORM = { rating_code: '', rating_name: '', rating_value: '', description: '' };
const COLUMNS = ['Code', 'Name', 'Value', 'Description', 'Actions'];
const PAGE_SIZE = 15;

export default function ConductRatings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [editingRating, setEditingRating] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const fetchRatings = useCallback(async () => {
    setLoading(true);
    try {
      setRatings(extractCollection(await reportCardAPI.getConductRatings()));
    } catch (e) {
      showSnackbar(getErrorMessage(e, 'Failed to load ratings'), 'error');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRatings(); }, [fetchRatings]);

  const resetForm = () => { setForm(INITIAL_FORM); setEditingRating(null); setDialog(false); };

  const handleEdit = (r) => {
    setEditingRating(r);
    setForm({
      rating_code: r.rating_code || r.code || '',
      rating_name: r.rating_name || r.name || '',
      rating_value: r.rating_value ?? '',
      description: r.description || ''
    });
    setDialog(true);
  };

  const handleDelete = (r) => {
    confirm({
      title: 'Delete Conduct Rating',
      message: `Delete "${getRatingName(r)}"? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await reportCardAPI.deleteConductRating(r.id);
          showSnackbar('Rating deleted successfully');
          fetchRatings();
        } catch (e) {
          showSnackbar(getErrorMessage(e, 'Failed to delete rating'), 'error');
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const name = form.rating_name.trim();
    if (!name || form.rating_value === '') { showSnackbar('Rating name and value are required', 'error'); return; }
    const code = (form.rating_code || name).toUpperCase().replace(/\s+/g, '_').slice(0, 20);
    setSaving(true);
    try {
      const payload = {
        rating_code: code, rating_name: name, name,
        rating_value: Number(form.rating_value), value: Number(form.rating_value),
        description: form.description || null, is_active: true
      };
      if (editingRating) {
        await reportCardAPI.updateConductRating(editingRating.id, payload);
        showSnackbar('Rating updated successfully');
      } else {
        await reportCardAPI.createConductRating(payload);
        showSnackbar('Rating created successfully');
      }
      resetForm();
      fetchRatings();
    } catch (e) {
      showSnackbar(getErrorMessage(e, 'Failed to save rating'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const total = ratings.length;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, lastPage);
  const paged = ratings.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pagination = { current_page: currentPage, last_page: lastPage, per_page: pageSize, total };

  return (
    <Box>
      <ModulePageHeader
        title="Conduct Ratings"
        subtitle="Manage conduct rating values (e.g. Excellent, Good, Fair, Poor)"
        actions={[
          <Button key="refresh" variant="outlined" startIcon={<Refresh />} onClick={fetchRatings} disabled={loading} fullWidth={isMobile}>Refresh</Button>,
          <Button key="bulk" variant="outlined" startIcon={<Add />} onClick={() => setBulkDialog(true)} fullWidth={isMobile}>Bulk Add</Button>,
          <Button key="add" variant="contained" startIcon={<Add />} onClick={() => setDialog(true)} fullWidth={isMobile}>Add Rating</Button>
        ]}
      />

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center"><CircularProgress size={28} /></Box>
          ) : ratings.length === 0 ? (
            <EmptyRecordsState message="No conduct ratings found." />
          ) : isMobile ? (
            <>
              <ResponsiveRecordList
                items={paged}
                renderItem={(r) => (
                  <Stack spacing={0.75}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight={700}>{getRatingName(r)}</Typography>
                      <Chip label={`Value: ${r.rating_value ?? '-'}`} size="small" color="primary" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">Code: {getRatingCode(r)}</Typography>
                    <Typography variant="body2" color="text.secondary">{r.description || 'No description'}</Typography>
                    <Box textAlign="right">
                      <IconButton size="small" color="primary" onClick={() => handleEdit(r)}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(r)}><DeleteIcon /></IconButton>
                    </Box>
                  </Stack>
                )}
              />
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} isMobile />
            </>
          ) : (
            <>
              <DataTable columns={COLUMNS} data={paged} loading={false} emptyMessage="No conduct ratings found." minWidth={600}
                renderRow={(r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={TD_SX}><strong>{getRatingCode(r)}</strong></TableCell>
                    <TableCell sx={TD_SX}>{getRatingName(r)}</TableCell>
                    <TableCell sx={TD_SX}><Chip label={r.rating_value ?? '-'} size="small" color="primary" /></TableCell>
                    <TableCell sx={TD_SX}>{r.description || '-'}</TableCell>
                    <TableCell sx={TD_SX}>
                      <IconButton size="small" color="primary" onClick={() => handleEdit(r)}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(r)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                )}
              />
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} isMobile={isMobile} />
            </>
          )}
        </CardContent>
      </Card>

      <ConductRatingFormDialog open={dialog} onClose={resetForm} onSubmit={handleSubmit}
        editingRating={editingRating} formData={form} setFormData={setForm} saving={saving} isMobile={isMobile} />

      <BulkConductRatingDialog
        open={bulkDialog}
        onClose={() => setBulkDialog(false)}
        saving={savingBulk}
        isMobile={isMobile}
        onSubmit={async (rows, onSuccess) => {
          setSavingBulk(true);
          try {
            await Promise.all(rows.map((r) => {
              const name = r.rating_name.trim();
              const code = (r.rating_code || name).toUpperCase().replace(/\s+/g, '_').slice(0, 20);
              return reportCardAPI.createConductRating({
                rating_code: code, rating_name: name, name,
                rating_value: Number(r.rating_value), value: Number(r.rating_value),
                description: r.description || null, is_active: true
              });
            }));
            showSnackbar(`${rows.length} rating${rows.length === 1 ? '' : 's'} created successfully`);
            setBulkDialog(false);
            onSuccess();
            fetchRatings();
          } catch (e) {
            showSnackbar(getErrorMessage(e, 'Bulk creation failed'), 'error');
          } finally {
            setSavingBulk(false);
          }
        }}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
}
